import { useMemo } from "react"
import { motion } from "framer-motion"
import { X } from "lucide-react"

import {
  usePvpRoomParties,
  usePvpRoomPhase,
  usePvpRoomWinner,
  usePvpRoomMyBetPartyId,
  usePvpRoomActiveBets,
} from "../hooks/usePvpRoomSelectors"
import { usePlayersListData } from "../hooks/usePlayersListData"
import {
  formatPartyHeaderName,
  formatUserDisplayName,
  isTeamParty,
  partyKey,
  userDataFromPlayerBanks,
} from "../lib/playersListUtils"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"

import { getUserIdFromToken } from "@/shared/api/auth"
import { cn } from "@/shared/lib/utils"
import { useTelegramUser } from "@/shared/lib/useTelegramUser"
import { resolveAvatarLoadUrl } from "@/shared/lib/avatarProxy"
import { formatTonCompact, toSafeNumber } from "@/shared/lib/pvpRouletteMath"
import { getAvatarInitials } from "@/shared/lib/avatarInitials"

import { useI18n } from "@/shared/i18n"

import { GiftBetThumbnails, type GiftThumbnailItem } from "./GiftBetThumbnails"
import type { AggregatedGiftBet } from "../lib/playersListUtils"

const toThumbItems = (gs: AggregatedGiftBet[]): GiftThumbnailItem[] =>
  gs.map((g) => ({
    id: g.betId,
    smallImageUrl: g.smallImageUrl,
  }))

interface PlayersListProps {
  roomId: string
  currentUserId?: string
}

export const PlayersList = ({ roomId, currentUserId }: PlayersListProps) => {
  const { t } = useI18n()

  const userId = currentUserId ?? getUserIdFromToken()
  const parties = usePvpRoomParties(roomId)
  const phase = usePvpRoomPhase(roomId)
  const winner = usePvpRoomWinner(roomId)
  const myBetPartyId = usePvpRoomMyBetPartyId(roomId, userId)
  const activeBets = usePvpRoomActiveBets(roomId)
  const telegramUser = useTelegramUser()

  const {
    aggregatedByPartyKey,
    cancelBetsByIds,
    cancellingRowKey,
    canCancelPhase,
  } = usePlayersListData(activeBets, phase)

  // Split into solo and squad (team)
  const { soloParties, collabParties } = useMemo(() => {
    const solo: typeof parties = []
    const collab: typeof parties = []
    for (const p of parties) {
      if (isTeamParty(p)) collab.push(p)
      else solo.push(p)
    }
    return { soloParties: solo, collabParties: collab }
  }, [parties])

  const cancelDisabled = cancellingRowKey != null
  const proxyAvatar = (url: string | null | undefined): string | null =>
    url ? resolveAvatarLoadUrl(url) : null

  // Render a solo player row — compact card style
  const renderSoloRow = (party: (typeof parties)[0], idx: number) => {
    const pKey = partyKey(party)
    const aggregated = aggregatedByPartyKey.get(pKey) ?? []
    const isMeSolo = userId != null && party.userId === userId
    const isMyParty =
      !!myBetPartyId &&
      (party.partyId === myBetPartyId ||
        party.userId === myBetPartyId ||
        (party.userId === userId && myBetPartyId === `solo:${userId}`))
    const isWinner =
      phase === "RESOLVED" &&
      winner &&
      (winner.partyId === party.partyId || winner.partyId === party.userId)

    if (aggregated.length > 0) {
      return aggregated.map((row, j) => {
        const isBetMe = userId != null && row.userData.userId === userId
        const displayName = formatUserDisplayName(
          row.userData,
          !!isBetMe,
          telegramUser
        )
        const photo = proxyAvatar(
          row.userData.photoUrl ??
            userDataFromPlayerBanks(party, row.userData.userId)?.photoUrl ??
            null
        )
        const amount = row.totalTon

        return (
          <motion.div
            key={row.userData.userId}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.03 + j * 0.02 }}
            onClick={() => {
              if (isBetMe && canCancelPhase && !cancelDisabled) {
                cancelBetsByIds(row.betIds, `solo:${row.userData.userId}`)
              }
            }}
            className={cn(
              "flex items-center gap-2.5 rounded-xl bg-secondary-main px-3 py-2",
              isWinner && "ring-1 ring-green-100",
              isMyParty && !isWinner && "ring-1 ring-green-100/20",
              isBetMe && canCancelPhase && "cursor-pointer active:opacity-70"
            )}
          >
            <Avatar className="h-7 w-7 shrink-0">
              <AvatarImage src={photo ?? undefined} />
              <AvatarFallback className="bg-tertiary-main text-[9px]">
                {getAvatarInitials(displayName)}
              </AvatarFallback>
            </Avatar>
            <span
              className={cn(
                "min-w-0 flex-1 truncate text-xs font-medium",
                isBetMe ? "text-green-100" : "text-white"
              )}
            >
              {displayName}
            </span>
            {row.giftBets.length > 0 && (
              <GiftBetThumbnails gifts={toThumbItems(row.giftBets)} tone="row" />
            )}
            <span className="shrink-0 text-xs font-semibold tabular-nums text-foreground">
              {formatTonCompact(amount)} {t("common.ton")}
            </span>
            {isBetMe && canCancelPhase && (
              <X className="h-3.5 w-3.5 shrink-0 text-white/60" />
            )}
          </motion.div>
        )
      })
    }

    // Fallback — party with no detailed bets
    const headerName = formatPartyHeaderName(party, !!isMeSolo, telegramUser)
    const betAmount = toSafeNumber(party.betAmount, 0)

    return (
      <motion.div
        key={pKey}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: idx * 0.03 }}
        className="flex items-center gap-2.5 rounded-xl bg-secondary-main px-3 py-2"
      >
        <div className="h-7 w-7 shrink-0 rounded-full bg-tertiary-main" />
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-green-100">
          {headerName}
        </span>
        <span className="shrink-0 text-xs font-semibold tabular-nums text-white">
          {formatTonCompact(betAmount)} {t("common.ton")}
        </span>
      </motion.div>
    )
  }

  // Render squad party — "Total" header + member rows
  const renderCollabParty = (party: (typeof parties)[0], idx: number) => {
    const pKey = partyKey(party)
    const aggregated = aggregatedByPartyKey.get(pKey) ?? []
    const totalAmount = toSafeNumber(party.betAmount, 0)
    const isWinner =
      phase === "RESOLVED" &&
      winner &&
      (winner.partyId === party.partyId || winner.partyId === party.userId)

    return (
      <div
        key={pKey}
        className={cn(
          "overflow-hidden rounded-xl bg-secondary-main",
          isWinner && "ring-1 ring-accent/40"
        )}
      >
        {/* Total header */}
        <div className="flex items-center justify-between bg-tertiary-main px-3 py-2">
          <span className="text-xs font-semibold text-white">
            {t("pvpRoom.total")}
          </span>
          <span className="text-xs font-bold tabular-nums text-white">
            {formatTonCompact(totalAmount)} {t("common.ton")}
          </span>
        </div>

        {/* Members */}
        <div className="divide-y divide-border/20">
          {aggregated.map((row, j) => {
            const isBetMe = userId != null && row.userData.userId === userId
            const displayName = formatUserDisplayName(
              row.userData,
              !!isBetMe,
              telegramUser
            )
            const photo = proxyAvatar(row.userData.photoUrl ?? null)
            const amount = row.totalTon

            return (
              <motion.div
                key={row.userData.userId}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 + j * 0.02 }}
                onClick={() => {
                  if (isBetMe && canCancelPhase && !cancelDisabled) {
                    cancelBetsByIds(row.betIds, `squad:${row.userData.userId}`)
                  }
                }}
                className={cn(
                  "flex items-center gap-2.5 px-3 py-2",
                  isBetMe &&
                    canCancelPhase &&
                    "cursor-pointer active:opacity-70"
                )}
              >
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarImage src={photo ?? undefined} />
                  <AvatarFallback className="bg-muted text-[9px]">
                    {getAvatarInitials(displayName)}
                  </AvatarFallback>
                </Avatar>
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate text-xs font-medium",
                    isBetMe ? "text-green-100" : "text-white"
                  )}
                >
                  @
                  {displayName.length > 8
                    ? displayName.slice(0, 8) + "…"
                    : displayName}
                </span>
                {row.giftBets.length > 0 && (
                  <GiftBetThumbnails gifts={toThumbItems(row.giftBets)} tone="row" />
                )}
                <span className="shrink-0 text-xs font-semibold tabular-nums text-white">
                  {formatTonCompact(amount)} {t("common.ton")}
                </span>
                {isBetMe && canCancelPhase && (
                  <X className="h-3.5 w-3.5 shrink-0 text-white/60" />
                )}
              </motion.div>
            )
          })}
        </div>
      </div>
    )
  }

  const emptyPlaceholder = (
    <div className="flex h-11 items-center gap-2 rounded-xl bg-secondary-main px-3">
      <div className="h-7 w-7 shrink-0 rounded-full bg-tertiary-main" />
      <span className="text-xs text-white">{t("pvpRoom.waitingForBets")}</span>
    </div>
  )

  return (
    <div>
      {/* Separator line */}
      <div className="my-3 border-t border-border/40" />

      {/* Always show Solo | Squad split */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h3 className="mb-2 text-lg/none font-semibold tracking-[-0.5px] text-white">
            {t("pvpRoom.solo")}
          </h3>
          <div className="space-y-1.5">
            {soloParties.length > 0
              ? soloParties.map((p, i) => renderSoloRow(p, i))
              : emptyPlaceholder}
          </div>
        </div>
        <div>
          <h3 className="mb-2 text-lg/none font-semibold tracking-[-0.5px] text-white">
            {t("pvpRoom.squad")}
          </h3>
          <div className="space-y-1.5">
            {collabParties.length > 0
              ? collabParties.map((p, i) => renderCollabParty(p, i))
              : emptyPlaceholder}
          </div>
        </div>
      </div>
    </div>
  )
}
